import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Check, Sparkles, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import InlineBirthForm from "../components/InlineBirthForm";
import { SEO_PAGES, SEO_NAV } from "../lib/seoPages";

// One component, six search-intent landing pages. Full copy + FAQ schema +
// a live birth form, so the visitor never has to go looking for the product.
export default function SeoLandingPage({ slug: slugProp }) {
  const params = useParams();
  const navigate = useNavigate();
  const slug = slugProp || params.slug;
  const page = SEO_PAGES[slug];
  if (!page) return null;

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        name: page.h1,
        description: page.description,
        url: `https://app.theastrofate.com/${page.slug}`,
        inLanguage: "en",
        isPartOf: { "@type": "WebSite", name: "AstroFate", url: "https://app.theastrofate.com" },
      },
      {
        "@type": "Service",
        name: page.h1,
        description: page.description,
        serviceType: "Vedic astrology",
        provider: { "@type": "Organization", name: "AstroFate", url: "https://app.theastrofate.com" },
        areaServed: "Worldwide",
        offers: { "@type": "Offer", price: "0", priceCurrency: "INR", description: "Free Kundli and first AI question" },
      },
      {
        "@type": "FAQPage",
        mainEntity: page.faqs.map(([q, a]) => ({
          "@type": "Question", name: q,
          acceptedAnswer: { "@type": "Answer", text: a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://app.theastrofate.com/" },
          { "@type": "ListItem", position: 2, name: page.h1, item: `https://app.theastrofate.com/${page.slug}` },
        ],
      },
    ],
  };

  return (
    <div data-testid={`seo-page-${page.slug}`} style={{ minHeight: "100vh", background: "#120021", paddingTop: "92px", position: "relative", overflowX: "hidden" }}>
      <SEO title={page.title} description={page.description} schema={schema} image="/logo-badge.png" />

      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0 }}>
        <div style={{ position: "absolute", width: "620px", height: "620px", background: "radial-gradient(circle, rgba(91,30,140,0.22), transparent)", top: "-120px", right: "-160px", borderRadius: "50%" }} />
        <div style={{ position: "absolute", width: "440px", height: "440px", background: "radial-gradient(circle, rgba(255,209,102,0.08), transparent)", bottom: "-80px", left: "-120px", borderRadius: "50%" }} />
      </div>

      <div style={{ position: "relative", zIndex: 1, maxWidth: "1120px", margin: "0 auto", padding: "0 16px 70px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1.15fr) minmax(0,0.85fr)", gap: "38px", alignItems: "start" }} className="seo-grid">
          <div className="animate-fadeInUp">
            <span style={{
              display: "inline-flex", alignItems: "center", gap: "7px", marginBottom: "16px",
              background: "rgba(255,209,102,0.1)", border: "1px solid rgba(255,209,102,0.35)",
              borderRadius: "40px", padding: "6px 14px", color: "#FFD166", fontSize: "0.76rem", fontWeight: 700,
            }}>
              <Sparkles size={12} /> {page.kicker}
            </span>

            <h1 className="font-cinzel" style={{ color: "white", fontWeight: 700, lineHeight: 1.18, marginBottom: "16px", fontSize: "clamp(1.65rem, 4.6vw, 2.9rem)" }}>
              {page.h1}
            </h1>

            <p style={{ color: "rgba(255,255,255,0.74)", fontSize: "1rem", lineHeight: 1.8, marginBottom: "26px", maxWidth: "640px" }}>
              {page.intro}
            </p>

            <ul style={{ listStyle: "none", padding: 0, margin: "0 0 30px", display: "flex", flexDirection: "column", gap: "11px" }}>
              {page.bullets.map((b) => (
                <li key={b} style={{ display: "flex", gap: "10px", color: "rgba(255,255,255,0.8)", fontSize: "0.92rem", lineHeight: 1.6 }}>
                  <Check size={16} style={{ color: "#86efac", flexShrink: 0, marginTop: "3px" }} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div style={{ display: "flex", gap: "11px", flexWrap: "wrap", marginBottom: "40px" }}>
              <button data-testid="seo-cta-kundli" className="btn-gold" onClick={() => navigate("/kundli")}>
                Get my free Kundli
              </button>
              <button data-testid="seo-cta-chat" className="btn-ghost" onClick={() => navigate("/chat")}>
                Ask one FREE chart question
              </button>
            </div>

            <div data-testid="seo-personalised-note" className="glass-card" style={{
              marginBottom: "30px", padding: "15px 17px",
              border: "1px solid rgba(255,209,102,0.32)",
              background: "linear-gradient(135deg, rgba(255,209,102,0.1), rgba(157,78,221,0.07))",
            }}>
              <p style={{ color: "#FFD166", fontWeight: 700, fontSize: "0.9rem", margin: "0 0 6px" }}>
                This page explains the general rules — your answer lives in your own chart
              </p>
              <p style={{ color: "rgba(255,255,255,0.72)", fontSize: "0.86rem", lineHeight: 1.7, margin: 0 }}>
                Nothing here is a prediction about you. Generate your free Kundli with the form on this page, then open
                <strong style={{ color: "#fff" }}> your report</strong> for the personalised reading — your Lagna,
                planet placements, Life Timeline and the exact Dasha you are in — and ask your own question there.
              </p>
              <div style={{ display: "flex", gap: "9px", flexWrap: "wrap", marginTop: "12px" }}>
                <button data-testid="seo-note-report-btn" className="btn-gold" style={{ padding: "9px 17px", fontSize: "0.82rem" }}
                  onClick={() => navigate("/kundli/report")}>Open my report</button>
                <button data-testid="seo-note-chat-btn" className="btn-ghost" style={{ padding: "9px 17px", fontSize: "0.82rem" }}
                  onClick={() => navigate("/chat")}>Ask about my chart</button>
              </div>
            </div>

            <h2 className="font-cinzel" style={{ color: "#FFD166", fontSize: "1.25rem", marginBottom: "16px" }}>
              Frequently asked questions
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "36px" }}>
              {page.faqs.map(([q, a], i) => (
                <details key={q} data-testid={`seo-faq-${i}`} className="glass-card" style={{ padding: "14px 16px", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <summary style={{ color: "#fff", fontWeight: 700, fontSize: "0.92rem", cursor: "pointer" }}>{q}</summary>
                  <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.88rem", lineHeight: 1.75, margin: "10px 0 0" }}>{a}</p>
                </details>
              ))}
            </div>

            <h2 className="font-cinzel" style={{ color: "#FFD166", fontSize: "1.1rem", marginBottom: "12px" }}>
              Explore more
            </h2>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "9px" }}>
              {SEO_NAV.filter(([href]) => href !== `/${page.slug}`).map(([href, label]) => (
                <Link key={href} data-testid={`seo-related-${href.slice(1)}`} to={href} style={{
                  display: "inline-flex", alignItems: "center", gap: "6px", textDecoration: "none",
                  background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,209,102,0.22)",
                  borderRadius: "40px", padding: "8px 15px", color: "rgba(255,255,255,0.78)", fontSize: "0.82rem", fontWeight: 600,
                }}>
                  {label} <ArrowRight size={12} style={{ color: "#FFD166" }} />
                </Link>
              ))}
            </div>
          </div>

          <div className="animate-fadeInUp delay-200" style={{ position: "relative" }}>
            <InlineBirthForm
              title="Your free Kundli"
              subtitle="Birth date, time and city — your chart and report come back in about 20 seconds."
              onConnected={() => navigate("/kundli/report")}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
